import {Label, NumberInput, Separator, Switch, useFormContext} from 'platform/components';
import {HStack, Show, VStack} from 'platform/foundation';

import {useState} from 'react';
import {Controller, useFormState, useWatch} from 'react-hook-form';

import {isNotNil} from 'ramda';

import {suffixTestId, type TestIdProps} from 'shared/web';

import i18n from '../../../../../i18n/i18n';
import {NumericBoundBody} from '../../../../../types/api/api';
import {SeparatorWithLabel} from '../../../../SeparatorWithLabel';
import type {NumericBoundFieldErrorState} from '../utils/mergeNumericBoundFieldError';
import {PresetEditorFormValues} from '../utils/presetEditorFormSchema';
import {BoundaryRow} from './BoundaryRow';

interface BoundariesSectionProps extends TestIdProps {
  isReadOnly?: boolean;
}

type PairUnit = 'abs' | 'pct';

interface PairRowProps extends TestIdProps {
  label: string;
  abs: NumericBoundBody | null;
  pct: NumericBoundBody | null;
  absLabel: string;
  onPairChange: (abs: NumericBoundBody | null, pct: NumericBoundBody | null) => void;
  isReadOnly?: boolean;
  absError: NumericBoundFieldErrorState | undefined;
  pctError: NumericBoundFieldErrorState | undefined;
}

function PairBoundaryRow(props: PairRowProps) {
  const {label, abs, pct, absLabel, onPairChange, isReadOnly, absError, pctError} = props;
  const [unit, setUnit] = useState<PairUnit>(isNotNil(pct) ? 'pct' : 'abs');

  const activeValue = unit === 'pct' ? pct : abs;
  const activeError = unit === 'pct' ? pctError : absError;

  const handleChange = (next: NumericBoundBody | null) => {
    if (unit === 'pct') {
      onPairChange(null, next);
    } else {
      onPairChange(next, null);
    }
  };

  const handleUnitChange = (u: PairUnit) => {
    setUnit(u);
    if (u === 'pct') {
      onPairChange(null, activeValue);
    } else {
      onPairChange(activeValue, null);
    }
  };

  return (
    <BoundaryRow
      label={label}
      value={activeValue}
      onChange={handleChange}
      unitLabels={{abs: absLabel, pct: '%'}}
      unit={unit}
      onUnitChange={handleUnitChange}
      isReadOnly={isReadOnly}
      errorMessage={activeError}
      data-testid={props['data-testid']}
    />
  );
}

export function BoundariesSection(props: BoundariesSectionProps) {
  const {isReadOnly} = props;
  const {control, setValue} = useFormContext<PresetEditorFormValues>();
  const {errors} = useFormState({control});

  const [mileage, mileagePct, power, powerPct, price, pricePct, radius] = useWatch({
    control,
    name: ['mileage', 'mileagePct', 'power', 'powerPct', 'price', 'pricePct', 'radius'],
  });

  const [radiusEnabled, setRadiusEnabled] = useState(isNotNil(radius));

  const handleRadiusToggle = (enabled: boolean) => {
    setRadiusEnabled(enabled);
    setValue('radius', enabled ? radius : null, {shouldDirty: true, shouldValidate: false});
  };

  return (
    <VStack spacing={3}>
      <SeparatorWithLabel label={i18n.t('entity.filterForm.savedFilters.dialog.boundaries')} />

      <Controller
        name="year"
        control={control}
        render={({field, fieldState}) => (
          <BoundaryRow
            label={i18n.t('entity.filterForm.registration')}
            value={field.value}
            onChange={field.onChange}
            isReadOnly={isReadOnly}
            errorMessage={fieldState.error}
            data-testid={suffixTestId('boundaryYear', props)}
          />
        )}
      />

      <Separator spacing={1} />

      <PairBoundaryRow
        label={i18n.t('entity.filterForm.millage')}
        abs={mileage}
        pct={mileagePct}
        absLabel="km"
        onPairChange={(nextMileage, nextMileagePct) => {
          setValue('mileage', nextMileage, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
          setValue('mileagePct', nextMileagePct, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
        }}
        isReadOnly={isReadOnly}
        absError={errors.mileage}
        pctError={errors.mileagePct}
        data-testid={suffixTestId('boundaryMileage', props)}
      />

      <Separator spacing={1} />

      <PairBoundaryRow
        label={i18n.t('entity.filterForm.power')}
        abs={power}
        pct={powerPct}
        absLabel="kW"
        onPairChange={(nextPower, nextPowerPct) => {
          setValue('power', nextPower, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
          setValue('powerPct', nextPowerPct, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
        }}
        isReadOnly={isReadOnly}
        absError={errors.power}
        pctError={errors.powerPct}
        data-testid={suffixTestId('boundaryPower', props)}
      />

      <Separator spacing={1} />

      <PairBoundaryRow
        label={i18n.t('entity.filterForm.price')}
        abs={price}
        pct={pricePct}
        absLabel="EUR"
        onPairChange={(nextPrice, nextPricePct) => {
          setValue('price', nextPrice, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
          setValue('pricePct', nextPricePct, {
            shouldDirty: true,
            shouldTouch: true,
            shouldValidate: true,
          });
        }}
        isReadOnly={isReadOnly}
        absError={errors.price}
        pctError={errors.pricePct}
        data-testid={suffixTestId('boundaryPrice', props)}
      />

      <Separator spacing={1} />

      <Controller
        name="featureScore"
        control={control}
        render={({field, fieldState}) => (
          <BoundaryRow
            label={i18n.t('entity.filterForm.featureScore')}
            value={field.value}
            onChange={field.onChange}
            unitLabels={{abs: '%', pct: '%'}}
            isReadOnly={isReadOnly}
            errorMessage={fieldState.error}
            data-testid={suffixTestId('boundaryFeatureScore', props)}
          />
        )}
      />

      <Separator spacing={1} />

      <HStack align="center" justify="space-between" spacing={2}>
        <HStack align="center" spacing={2}>
          <Switch value={radiusEnabled} onChange={handleRadiusToggle} isDisabled={isReadOnly} />
          <Label>{i18n.t('entity.filterForm.radius')}</Label>
        </HStack>
      </HStack>
      <Show when={radiusEnabled}>
        <HStack>
          <Controller
            name="radius"
            control={control}
            render={({field, fieldState}) => (
              <NumberInput
                value={field.value ?? null}
                onChange={(km) => field.onChange(km ?? null)}
                onBlur={field.onBlur}
                isDisabled={isReadOnly}
                decimalPlaces={0}
                suffix="km"
                errorMessage={fieldState.error?.message}
                isInvalid={!!fieldState.error}
              />
            )}
          />
        </HStack>
      </Show>
    </VStack>
  );
}
